import {
  type Query,
  type QueryClient,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { useEffect, useMemo, useReducer } from 'react';
import {
  RECOVERY_POLL_LIMIT_MS,
  conversationRecovery,
  hasSavedDispatchedTurn,
} from '../lib/conversation-recovery';
import {
  clearDispatchedTurn,
  readDispatchedTurn,
} from '../lib/dispatched-turn';

import { assistantApiUrl, assistantRequestInit } from '@/lib/assistant-request';
import type { ConversationWithMessages } from '../types';

export const CONVERSATION_READ_TIMEOUT_MS = 30_000;

async function readConversation(
  id: string,
  signal?: AbortSignal
): Promise<ConversationWithMessages> {
  if (signal?.aborted) throw signal.reason;
  const controller = new AbortController();
  let rejectRead!: (error: unknown) => void;
  const interrupted = new Promise<never>((_, reject) => {
    rejectRead = reject;
  });
  const abortRead = (error: unknown) => {
    controller.abort(error);
    rejectRead(error);
  };
  const cancel = () =>
    abortRead(signal?.reason ?? new Error('Conversation read cancelled'));
  signal?.addEventListener('abort', cancel, { once: true });
  const timeout = setTimeout(() => {
    abortRead(new Error('Conversation read timed out. Try again.'));
  }, CONVERSATION_READ_TIMEOUT_MS);
  try {
    // Bound both the connection and the response body. Racing cancellation
    // also prevents a late response from updating receipts/cache after abort.
    const pending = (async (): Promise<ConversationWithMessages> => {
      const res = await fetch(
        assistantApiUrl(`conversations/${id}`),
        assistantRequestInit({ signal: controller.signal })
      );
      if (!res.ok) throw new Error('Failed to load conversation');
      return res.json();
    })();
    const conversation = await Promise.race([pending, interrupted]);
    const receipt = readDispatchedTurn(id);
    if (receipt && hasSavedDispatchedTurn(conversation, receipt))
      clearDispatchedTurn(id);
    return conversation;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', cancel);
  }
}

export const conversationQueryOptions = (
  id: string,
  recoveryDeadline = Date.now() + RECOVERY_POLL_LIMIT_MS
) =>
  queryOptions({
    queryKey: ['assistant', 'conversations', id],
    queryFn: (context): Promise<ConversationWithMessages> =>
      readConversation(id, context.signal),
    enabled: !!id,
    staleTime: 60 * 1000,
    retry: false,
    refetchOnMount: 'always',
    refetchInterval: (query: Query<ConversationWithMessages>) => {
      if (query.state.status === 'error') return false;
      const recovery = conversationRecovery(
        query.state.data,
        readDispatchedTurn(id),
        recoveryDeadline
      );
      return recovery === 'running' || recovery === 'checking' ? 2000 : false;
    },
  });

export const useConversation = (id: string) => {
  const { deadline } = useMemo(
    () => ({ id, deadline: Date.now() + RECOVERY_POLL_LIMIT_MS }),
    [id]
  );
  const [, checkDeadline] = useReducer((value: number) => value + 1, 0);
  useEffect(() => {
    if (!id) return;
    // Network events may stop entirely while offline or while a read stalls.
    // The visible recovery deadline must still advance on its own.
    const timer = setTimeout(checkDeadline, Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [id, deadline]);
  const options = useMemo(
    () => conversationQueryOptions(id, deadline),
    [id, deadline]
  );
  const query = useQuery(options);
  const recovery =
    query.isError && query.data
      ? 'unavailable'
      : conversationRecovery(query.data, readDispatchedTurn(id), deadline);
  return {
    conversation: query.data ?? null,
    messages: query.data?.messages ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    recovery,
  };
};

/** Resolve the persisted baseline before dispatch, including after rejected local turns. */
export async function fetchConversationForDispatch(
  client: QueryClient,
  id: string
) {
  // Do not deduplicate against polling that started before the previous turn
  // was saved: this request must start after the owner chooses to send.
  const conversation = await readConversation(id);
  client.setQueryData(conversationQueryOptions(id).queryKey, conversation);
  return conversation;
}
